import 'react-phone-number-input/style.css'

import { useDirection } from '@radix-ui/react-direction'
import clsx from 'clsx'
import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Country,
  DefaultInputComponentProps,
  parsePhoneNumber
} from 'react-phone-number-input'
import PhoneInputWithCountry, { Props } from 'react-phone-number-input/input'

import Input from '@/components-latest/primitives/Controls/Input'
import CountryNumberSelect from '@/components-latest/primitives/CountryNumberSelect'

import Styles from './PhoneInput.module.scss'

type DefaultProps = {
  isError?: boolean
  zIndex?: number
}
const PhoneInput = React.memo(
  forwardRef<
    HTMLDivElement | null,
    Props<DefaultInputComponentProps> & DefaultProps
  >((props, ref) => {
    const {
      isError,
      onChange: onPhoneChange,
      country: _country,
      value,
      zIndex,
      className,
      disabled,
      defaultCountry,
      ...restProps
    } = props

    const dir = useDirection()

    const { t } = useTranslation()

    const [country, setCountry] = useState<Country | undefined>()
    const onCountryChange = useCallback((value: Country) => {
      setCountry(value)
    }, [])

    const onPhoneChangeEnhanced = useCallback(
      (value?: string) => {
        onPhoneChange(value)

        if (!value) return

        const parsedPhoneNumber = parsePhoneNumber(value)

        if (
          parsedPhoneNumber?.country &&
          parsedPhoneNumber.country !== country
        ) {
          setCountry(parsedPhoneNumber.country)
        }
      },
      [onPhoneChange, country]
    )

    useEffect(() => {
      onPhoneChangeEnhanced(value as string)
    }, [value])

    return (
      <div
        className={clsx(Styles.wrapper, className)}
        style={{ zIndex: zIndex ?? 5 }}>
        <CountryNumberSelect
          country={country || defaultCountry}
          disabled={disabled}
          dir={dir}
          className={clsx(Styles.countrySelect)}
          onChange={onCountryChange}
        />
        <PhoneInputWithCountry
          country={country}
          className={clsx(Styles.input, {
            [Styles.error]: isError
          })}
          disabled={disabled}
          defaultCountry={defaultCountry}
          international={true}
          inputComponent={Input}
          data-testid="phone-input"
          placeholder={t('common.phone-input.placeholder')}
          value={value}
          {...restProps}
          onChange={onPhoneChangeEnhanced}
        />
      </div>
    )
  })
)

export default PhoneInput
